import { create } from 'zustand';
import { getWaiters } from '../../services/waiterApi.js';

export const useWaiterStore = create((set, get) => ({
  waiters: [],
  loading: false,
  error: null,
  currentPage: 1,
  pageSize: 10,
  totalCount: 0,
  hasLoaded: false,
  lastFetched: null,

  setWaiters: (waiters) => set({ waiters }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setCurrentPage: (page) => set({ currentPage: page }),
  setPageSize: (size) => set({ pageSize: size, currentPage: 1 }),

  // Load initial data
  loadWaiters: async (forceReload = false) => {
    const { hasLoaded, loading, lastFetched } = get();
    
    // Don't reload if already loaded and not forcing reload (cache for 5 minutes)
    if (hasLoaded && !forceReload && Date.now() - lastFetched < 5 * 60 * 1000) {
      return;
    }

    if (loading) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await getWaiters();
      const waitersData = response.waiters || [];

      set({
        waiters: waitersData,
        hasLoaded: true,
        lastFetched: Date.now(),
        currentPage: 1,
        totalCount: waitersData.length,
        loading: false,
        error: null
      });
    } catch (err) {
      set({
        error: err.message || 'Failed to load waiters',
        loading: false
      });
    }
  },

  // Get paginated waiters
  getPaginatedWaiters: (page = 1, pageSize = 10) => {
    const { waiters } = get();
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return waiters.slice(startIndex, endIndex);
  },

  // Get filtered and paginated waiters
  getFilteredAndPaginated: (filters = {}, page = 1, pageSize = 10) => {
    const { waiters } = get();
    let filtered = waiters;

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(waiter => 
        waiter.name.toLowerCase().includes(query) ||
        waiter.id.toString().includes(query)
      );
    }

    if (filters.sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (filters.sortBy === 'recent') {
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    return {
      data: filtered.slice(startIndex, endIndex),
      total: filtered.length,
      page,
      pageSize,
      pages: Math.ceil(filtered.length / pageSize)
    };
  },

  // Get waiter by ID
  getWaiterById: (id) => {
    const { waiters } = get();
    return waiters.find(w => w.id === id);
  },

  // Refresh all data
  refreshWaiters: async () => get().loadWaiters(true),

  // Add waiter to store
  addWaiter: (waiter) => set((state) => ({
    waiters: [waiter, ...state.waiters],
    totalCount: state.totalCount + 1
  })),

  // Update waiter in store
  updateWaiter: (id, updatedWaiter) => set((state) => ({
    waiters: state.waiters.map(w => w.id === id ? updatedWaiter : w)
  })),

  // Remove waiter from store
  removeWaiter: (id) => set((state) => ({
    waiters: state.waiters.filter(w => w.id !== id),
    totalCount: state.totalCount - 1
  })),

  // Clear store
  clearWaiters: () => set({
    waiters: [],
    hasLoaded: false,
    currentPage: 1,
    totalCount: 0,
    lastFetched: null
  })
}));

export default useWaiterStore;
